'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef, useState } from 'react';

/**
 * Tracks whether an element's content overflows its height, e.g. text cut off by `line-clamp`.
 * Attach the returned callback ref to the clamped element. The clamped box keeps its size while
 * its content reflows, so besides resizes it re-measures on content mutations and on images
 * loading inside it. Pass `enabled: false` while the clamp is lifted so the last clamped
 * measurement is kept.
 */
export function useIsClamped(t0) {
  const $ = _c(7);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  const [isClamped, setIsClamped] = useState(false);
  const cleanupRef = useRef(null);
  let t3;
  if ($[2] !== enabled) {
    t3 = element => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (!element || !enabled) {
        return;
      }
      const measure = () => setIsClamped(element.scrollHeight - element.clientHeight > 1);
      measure();
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(element);
      const mutationObserver = new MutationObserver(measure);
      mutationObserver.observe(element, {
        subtree: true,
        childList: true,
        characterData: true
      });
      element.addEventListener("load", measure, true);
      cleanupRef.current = () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        element.removeEventListener("load", measure, true);
      };
    };
    $[2] = enabled;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const ref = t3;
  let t4;
  if ($[4] !== isClamped || $[5] !== ref) {
    t4 = [ref, isClamped];
    $[4] = isClamped;
    $[5] = ref;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4 as const;
}